import { t } from "../../i18n";
import type { Artifact } from "@lumen/shared";
import { Download, FileText, Image, LoaderCircle, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../../api";
import { ArtifactMarkdownPreview } from "../../components/artifacts/ArtifactMarkdownPreview";
import { GameAssetPreview } from "../../components/game-assets/GameAssetPreview";
import {
  artifactDownloadUrl,
  artifactVaultErrorMessage,
  isMarkdownArtifact,
  safeArtifactUrl,
} from "../../components/artifacts/artifact-utils";

function sizeLabel(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function ArtifactsPage() {
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      setArtifacts(await api<Artifact[]>("/artifacts"));
    } catch (cause) {
      setError(artifactVaultErrorMessage(cause, "Vault could not be loaded"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const remove = async (artifact: Artifact) => {
    if (!window.confirm(`Delete ${artifact.name}?`)) return;
    try {
      await api(`/artifacts/${artifact.id}`, { method: "DELETE" });
      setArtifacts((current) =>
        current.filter((item) => item.id !== artifact.id),
      );
    } catch (cause) {
      setError(
        artifactVaultErrorMessage(cause, "Artifact could not be deleted"),
      );
    }
  };

  return (
    <section className="min-w-0 flex-1 overflow-y-auto p-5 md:p-8">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <h1 className="text-2xl font-bold tracking-tight">
            {t("Artifact vault")}
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-stone-500 dark:text-stone-400">
            {t(
              "Save artifacts from a conversation when you want to keep them here\n            privately for your account. Artifacts can be removed from this vault\n            without removing them from their original chat.",
            )}
          </p>
        </div>
        {error && (
          <div className="mb-5 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/20 dark:text-red-300">
            {error}
          </div>
        )}
        {loading ? (
          <div className="grid place-items-center py-20 text-stone-400">
            <LoaderCircle className="animate-spin" size={22} />
          </div>
        ) : artifacts.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-stone-300 p-10 text-center text-sm text-stone-500 dark:border-white/15 dark:text-stone-400">
            {t(
              "Your vault is empty. Ask a model to create an artifact, then use\n            Save to vault in the chat result.",
            )}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {artifacts.map((artifact) => (
              <article
                key={artifact.id}
                className="overflow-hidden rounded-2xl border border-stone-200 bg-white/70 dark:border-white/10 dark:bg-white/[0.03]"
              >
                {isMarkdownArtifact(artifact.name, artifact.mimeType) && (
                  <ArtifactMarkdownPreview
                    url={safeArtifactUrl(artifact.url)}
                    name={artifact.name}
                    downloadUrl={artifactDownloadUrl(
                      artifact.url,
                      artifact.downloadUrl,
                    )}
                  />
                )}
                {isMarkdownArtifact(
                  artifact.name,
                  artifact.mimeType,
                ) ? null : artifact.mimeType.startsWith("image/") &&
                  artifact.mimeType !== "image/svg+xml" &&
                  !/sprite|tileset|atlas/i.test(artifact.name) ? (
                  <a href={artifact.url} target="_blank" rel="noreferrer">
                    <img
                      src={artifact.url}
                      alt={artifact.name}
                      className="h-44 w-full object-cover"
                    />
                  </a>
                ) : artifact.mimeType.startsWith("model/") ||
                  /\.(glb|gltf|fbx|obj|usdz|stl)$/i.test(artifact.name) ||
                  /sprite|tileset|atlas/i.test(artifact.name) ? (
                  <div className="p-3">
                    <GameAssetPreview asset={artifact} />
                  </div>
                ) : (
                  <div className="grid h-44 place-items-center bg-stone-50 text-stone-400 dark:bg-white/[0.03]">
                    {artifact.mimeType.startsWith("text/") ? (
                      <FileText size={38} />
                    ) : (
                      <Image size={38} />
                    )}
                  </div>
                )}
                <div className="p-4">
                  <div className="truncate font-semibold" title={artifact.name}>
                    {artifact.name}
                  </div>
                  <div className="mt-1 truncate text-xs text-stone-500 dark:text-stone-400">
                    {artifact.mimeType} · {sizeLabel(artifact.sizeBytes)}
                  </div>
                  <code className="mt-2 block truncate text-[11px] text-lumen-700 dark:text-lumen-300">
                    {artifact.reference}
                  </code>
                  <div className="mt-4 flex items-center gap-2">
                    <a
                      className="button flex-1 justify-center"
                      href={safeArtifactUrl(artifact.url) || undefined}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("Open")}
                    </a>
                    <a
                      className="button p-2"
                      href={
                        artifactDownloadUrl(
                          artifact.url,
                          artifact.downloadUrl,
                        ) || undefined
                      }
                      title={t("Download")}
                    >
                      <Download size={16} />
                    </a>
                    <button
                      className="button p-2 text-red-600"
                      onClick={() => void remove(artifact)}
                      title={t("Delete")}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
